/* Admin — Moderasi Ulasan Produk: daftar, filter, sembunyikan/tampilkan, hapus. */
import React, { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, Trash2, Search, Image as ImageIcon, Video } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Stars } from "@/components/ProductReviews";

export default function AdminReviews() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [status, setStatus] = useState("");
  const [rating, setRating] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    api.get("/admin/reviews", { params: { status: status || undefined, rating: rating || undefined, q: q || undefined, page, limit: 20 } })
      .then((r) => setData(r.data)).catch(notifyError);
  }, [status, rating, q, page]);
  useEffect(() => { load(); }, [load]);

  const setVisible = async (r, visible) => {
    try {
      await api.patch(`/admin/reviews/${r.id}/status`, { status: visible ? "published" : "hidden" });
      toast.success(visible ? "Ulasan ditampilkan" : "Ulasan disembunyikan"); load();
    } catch (e) { notifyError(e); }
  };
  const remove = async (r) => {
    if (!window.confirm("Hapus ulasan ini secara permanen?")) return;
    try { await api.delete(`/admin/reviews/${r.id}`); toast.success("Ulasan dihapus"); load(); } catch (e) { notifyError(e); }
  };

  return (
    <AdminLayout title="Ulasan Produk" subtitle={`${data.total} ulasan`}>
      <div className="flex gap-2 flex-wrap mb-4" data-testid="admin-reviews-filters">
        <div className="relative"><Search size={14} className="absolute left-2 top-2.5 text-[#5C5C5C]" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Cari produk / isi ulasan" className="pl-7 w-64" data-testid="admin-reviews-search" /></div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="border rounded-md px-2 text-sm" data-testid="admin-reviews-status">
          <option value="">Semua status</option><option value="published">Tampil</option><option value="hidden">Disembunyikan</option>
        </select>
        <select value={rating} onChange={(e) => { setRating(e.target.value); setPage(1); }} className="border rounded-md px-2 text-sm" data-testid="admin-reviews-rating">
          <option value="">Semua rating</option>{[5, 4, 3, 2, 1].map((s) => <option key={s} value={s}>{s} ★</option>)}
        </select>
      </div>
      <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-[#5C5C5C] border-b"><tr>
            <th className="p-3">Produk</th><th className="p-3">Pelanggan</th><th className="p-3">Rating</th><th className="p-3">Ulasan</th><th className="p-3">Media</th><th className="p-3">Tanggal</th><th className="p-3">Status</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.id} className="border-b last:border-0 align-top" data-testid={`admin-review-${r.id}`}>
                <td className="p-3 font-semibold">{r.product?.slug ? <a href={`/produk/${r.product.slug}`} target="_blank" rel="noreferrer" className="underline">{r.product.name}</a> : r.product?.name || "-"}
                  {r.purchased_variant?.variant_label && <div className="text-[11px] text-[#5C5C5C]">Varian: {r.purchased_variant.variant_label}</div>}</td>
                <td className="p-3">{r.customer_name}{r.verified_purchase && <div className="text-[10px] text-[#2D6A4F]">Terverifikasi</div>}</td>
                <td className="p-3"><Stars value={r.rating} /></td>
                <td className="p-3 max-w-xs whitespace-pre-wrap break-words">{r.comment || <span className="text-[#5C5C5C] italic">—</span>}</td>
                <td className="p-3 text-xs">
                  {r.media?.images?.length > 0 && <span className="inline-flex items-center gap-1 mr-2"><ImageIcon size={12} /> {r.media.images.length}</span>}
                  {r.media?.video && <span className="inline-flex items-center gap-1"><Video size={12} /> 1</span>}
                </td>
                <td className="p-3 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString("id-ID") : ""}</td>
                <td className="p-3"><span className={`text-[11px] px-2 py-0.5 rounded-full ${r.status === "published" ? "bg-[#2D6A4F]/15 text-[#2D6A4F]" : "bg-[#E5E5E5] text-[#5C5C5C]"}`} data-testid={`admin-review-status-${r.id}`}>{r.status === "published" ? "Tampil" : "Disembunyikan"}</span></td>
                <td className="p-3 whitespace-nowrap">
                  {r.status === "published"
                    ? <Button size="sm" variant="ghost" onClick={() => setVisible(r, false)} title="Sembunyikan" data-testid={`admin-review-hide-${r.id}`}><EyeOff size={14} /></Button>
                    : <Button size="sm" variant="ghost" onClick={() => setVisible(r, true)} title="Tampilkan" data-testid={`admin-review-show-${r.id}`}><Eye size={14} /></Button>}
                  <Button size="sm" variant="ghost" className="text-[#DC2F02]" onClick={() => remove(r)} title="Hapus" data-testid={`admin-review-delete-${r.id}`}><Trash2 size={14} /></Button>
                </td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={8} className="p-6 text-center text-[#5C5C5C]" data-testid="admin-reviews-empty">Belum ada ulasan.</td></tr>}
          </tbody>
        </table>
      </div>
      {data.total > 20 && (
        <div className="flex gap-2 mt-3 justify-end">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Sebelumnya</Button>
          <Button variant="outline" size="sm" disabled={page * 20 >= data.total} onClick={() => setPage((p) => p + 1)}>Berikutnya</Button>
        </div>
      )}
    </AdminLayout>
  );
}
